import {
  __,
  append,
  assoc,
  compose,
  curry,
  eqBy,
  equals,
  filter,
  find,
  isEmpty,
  isNil,
  map,
  not,
  omit,
  path,
  pathOr,
  propEq,
  reduce,
  uniqWith,
  when,
} from 'ramda'
import { mergeArrays } from '../../utils/functions'

const appendBasketLines = (state, lines) => compose(
  assoc('basketLines', __, state.currentBasket),
  reduce(
    (list, item) => append(item, list),
    pathOr([], ['currentBasket', 'basketLines'], state),
  ),
)(lines)

const isNotNil = compose(not, isNil)

export default {
  setLoading(state, loading) {
    state.loading = loading
  },

  setLoadingAdditionalData(state, loading) {
    state.loadingAdditionalData = loading
  },

  setCurrentBasket(state, currentBasket) {
    state.currentBasket = currentBasket
  },

  setTotalsOnCurrentBasket(state, totals) {
    if (!state.currentBasket) {
      return
    }
    state.currentBasket = assoc('totals', totals, state.currentBasket)
  },

  /**
   * Adds new basket lines to the current basket.
   *
   * @param {Object} state
   * @param {Array} basketLines
   */
  addLinesToCurrentBasket(state, basketLines) {
    if (!state.currentBasket) {
      throw new Error('Current basket not found')
    }

    state.currentBasket = appendBasketLines(state, basketLines)
  },

  /**
   * Updates an existing basket line in the state
   *
   * @param {Object} state
   * @param {Object} basketLine
   */
  MERGE_LINE_INTO_BASKET(state, basketLine) {
    let lineFound = false

    state.currentBasket = compose(
      assoc('basketLines', __, state.currentBasket),
      map(__, pathOr([], ['currentBasket', 'basketLines'], state)),
      curry((id, existingLine) => {
        if (existingLine.id === id) {
          lineFound = true
          return basketLine
        }
        return existingLine
      }),
      path(['id']),
    )(basketLine)

    if (!lineFound) {
      state.currentBasket = appendBasketLines(state, [basketLine])
    }
  },

  /**
   * Removes a line from the state's current basket.
   *
   * @param {Object} state
   * @param {[Object]} basketLines
   */
  REMOVE_LINES_FROM_BASKET(state, basketLines) {
    const offerLineIris = map(path(['offerLine']), basketLines)
    let basket = state.currentBasket

    if (!basket) {
      return
    }

    // Remove basket lines from the basket.
    basket = compose(
      compose(
        assoc('basketLines', __, state.currentBasket),
        filter(compose(not, find(__, basketLines), propEq('id'), path(['id']))),
      ),
      pathOr([], ['basketLines']),
    )(basket)

    // Remove offer lines from the basket, including the offer itself if the lines array is empty.
    basket = compose(
      assoc('offers', __, basket),
      filter(isNotNil),
      map((offer) => compose(
        when(compose(isEmpty, path(['offerLines'])), () => null),
        assoc('offerLines', __, offer),
        filter(compose(not, find(__, offerLineIris), equals, path(['iri']))),
        pathOr([], ['offerLines']),
      )(offer)),
      pathOr([], ['offers']),
    )(basket)

    state.currentBasket = basket
  },

  /**
   * Adds offer lines to the current basket.
   *
   * Running this in the add basket lines routines improves the speed that the system responds to user inputs.
   *
   * @param state
   * @param offer
   * @param offerLines
   */
  addOfferLinesToCurrentBasket(state, { offer, offerLines }) {
    const { currentBasket } = state

    if (!currentBasket) {
      return
    }

    const iriMatches = propEq('iri', offer.iri)

    state.currentBasket = compose(
      assoc('offers', __, state.currentBasket),
      when(
        compose(not, find(iriMatches)),
        append(assoc('offerLines', offerLines, offer)),
      ),
      map(
        when(
          iriMatches,
          (basketOffer) => assoc(
            'offerLines',
            compose(
              uniqWith(eqBy(path(['iri']))),
              mergeArrays(offerLines),
              pathOr([], ['offerLines']),
            )(basketOffer),
            basketOffer,
          ),
        ),
      ),
      pathOr([], ['currentBasket', 'offers']),
    )(state)
  },

  /**
   * Marks a offer line as currently busy.
   *
   * This requires a promise that is the cause for the offer line being busy be included.
   *
   * @param {Object} state
   * @param {Number} offerLineId
   * @param {Promise} promise
   */
  MARK_OFFER_LINE_AS_BUSY(state, { offerLineId, promise }) {
    state.busyOfferLines = assoc(offerLineId, promise, state.busyOfferLines)
  },

  /**
   * Marks a offer line as no longer busy. Manages lines by offer line ID.
   *
   * @param {Object} state
   * @param {Number} offerLineId
   */
  UNMARK_OFFER_LINE_AS_BUSY(state, { offerLineId }) {
    state.busyOfferLines = omit([offerLineId], state.busyOfferLines)
  },

  /**
   * Sets the basketReloadPending value.
   *
   * @param {Object} state
   * @param {Boolean} basketReloadPending
   */
  SET_BASKET_RELOAD_PENDING(state, basketReloadPending) {
    state.basketReloadPending = basketReloadPending
  },

  /**
   * Sets the basketReloadQueued value.
   *
   * @param {Object} state
   * @param {Boolean} basketReloadQueued
   */
  SET_BASKET_RELOAD_QUEUED(state, basketReloadQueued) {
    state.basketReloadQueued = basketReloadQueued
  },
}
